import React, { JSX, ReactNode, useState } from "react";
import styled from "@emotion/styled";
import {
  Tabs as MuiTabs,
  Tab as MuiTab,
  Box,
  useMediaQuery,
  Theme,
  TabsProps as MuiTabsProps,
  Fade,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: 0 auto;
`;

export const TabsHeader = styled.div<{ centered: boolean }>`
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.secondary};
  ${({ centered }) =>
    centered &&
    `
    width: 100%;
    margin: 0 auto;
  `}
`;
export const StyledTabs = styled(MuiTabs, {
  shouldForwardProp: (prop) => prop !== "centered",
})<{ centered?: boolean }>`
  .MuiTabs-indicator {
    background-color: ${({ theme }) => theme.palette.primary.main};
  }

  ${({ centered }) =>
    centered &&
    `
    .MuiTabs-flexContainer {
      width: fit-content;
      margin: 0 auto;
    }
  `}
`;

export const StyledTab = styled(MuiTab)`
  padding: ${({ theme }) => theme.spacing(4, 3)};
  font-size: 16px;
  line-height: 24px;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding: ${({ theme }) => theme.spacing(3.5, 3)};
    font-size: 14px;
    line-height: 22px;
  }
`;

const Body = styled.div`
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

export interface TabsProps<T> extends Omit<MuiTabsProps, "onChange" | "value"> {
  defaultKey?: string | null;
  /** When set, the active tab is fully controlled by the parent (e.g. URL sync). */
  activeTabKey?: T;
  children: ReactNode[] | ReactNode;
  onChange?: (tabKey: T) => void;
  buttonElement?: JSX.Element;
  fakeDisabled?: string[];
  keepRendered?: boolean;
  centered?: boolean;
  noTransition?: boolean;
}

export const Tabs = <T extends string>({
  children,
  onChange,
  defaultKey,
  activeTabKey,
  buttonElement,
  fakeDisabled,
  centered = false,
  keepRendered = false,
  noTransition = false,
  ...rest
}: TabsProps<T>) => {
  const theme = useTheme();
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const chidrenArray = Array.isArray(children) ? children : [children];
  const validChildren = chidrenArray.filter((child) =>
    React.isValidElement(child)
  ) as React.ReactElement<{
    label: React.ReactNode;
    defaultActive?: boolean;
    testId?: string;
    disabled?: boolean;
  }>[];

  const defaultActiveChild = validChildren.find(
    ({ props }) => props.defaultActive
  );

  const [internalTab, setInternalTab] = useState<string>(
    defaultKey || ((defaultActiveChild?.key || validChildren[0].key) as string)
  );

  const isControlled = activeTabKey !== undefined;
  const activeTab = (isControlled ? activeTabKey! : internalTab) as string;

  const activeChild = validChildren.find((el) => el.key === activeTab);

  const handleChange = (event: React.SyntheticEvent, value: T) => {
    if (
      // @ts-ignore
      validChildren.find((el) => el.key === value)?.disabled ||
      fakeDisabled?.includes(value)
    )
      return;

    if (!isControlled) {
      setInternalTab(value);
    }
    if (onChange) {
      onChange(value);
    }
  };

  return (
    <Container>
      <TabsHeader centered={centered}>
        <StyledTabs
          value={activeTab}
          centered={centered}
          onChange={handleChange}
          {...rest}
        >
          {validChildren.map((el, index) => (
            <StyledTab
              disableRipple
              value={el.key}
              key={`label-${index}`}
              data-testid={el.props.testId}
              label={el.props.label}
              disabled={el.props.disabled}
              className={
                fakeDisabled?.includes(el.key as string) ? "Mui-disabled" : ""
              }
              style={{
                color:
                  activeTab === el.key
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
              }}
            />
          ))}
        </StyledTabs>
        {buttonElement && (
          <Box
            position="absolute"
            right={0}
            top={isLessSm ? "7px" : "10px"}
            zIndex="1"
          >
            {React.cloneElement(buttonElement, { activeTab })}
          </Box>
        )}
      </TabsHeader>
      <Body>
        <AnimatePresence mode="wait">
          {keepRendered ? (
            validChildren.map((child) => (
              <Fade
                in={child.key === activeTab}
                key={child.key}
                timeout={noTransition ? 0 : 400}
              >
                <Box
                  display={child.key === activeTab ? "block" : "none"}
                  key={child.key}
                >
                  {child}
                </Box>
              </Fade>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: noTransition ? 0 : 0.2 }}
              key={activeTab}
            >
              {activeChild}
            </motion.div>
          )}
        </AnimatePresence>
      </Body>
    </Container>
  );
};
